import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpEventType,
  HttpHeaders,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { ApiService } from '../../providers/api/api.service';
import { NotificationService } from '../../components/notifications/notifications.service';

export enum ERROR_MESSAGE {
  invalid = 'License is invalid',
  added = 'License is already added',
  common = 'License is not uploaded'
}

@Injectable({
  providedIn: 'root'
})
export class LicensesPageService {
  isActWebEnabled = false;
  activeTestLicenseDetails: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  isJmepResultsLicenseEnabled = false;
  private servicePrefix = 'licensing';
  private tenantId = 'paas.local';
  private lockCodePath = `/${this.servicePrefix}/lock-code`;
  private licenseDetailPath = `/${this.servicePrefix}/licenses`;
  private VERSION_URL = '/app-versions.json';

  constructor(
    private http: HttpClient,
    private notificationService: NotificationService,
    private readonly apiService: ApiService
  ) {}

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getLockCode(): Observable<any> {
    return this.apiService.get$(this.lockCodePath, {});
  }

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getLicenseCount(): Observable<any> {
    return this.apiService.get$(this.licenseDetailPath, {});
  }
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  setIsActWebLicense(isActWebEnabled: boolean): void {
    this.isActWebEnabled = isActWebEnabled;
  }
  getIsActWebLicense(): boolean {
    return this.isActWebEnabled;
  }
  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  getLicenseVersion(): Observable<any> {
    return this.apiService.get$(this.VERSION_URL, {});
  }

  uploadLicenseFile(files: Set<File>): {
    [key: string]: { progress: Observable<number>; error: string };
  } {
    // This will be the our resulting map
    const status: { [key: string]: { progress: Observable<number>; error: string } } = {};

    files.forEach(file => {
      // create a new multipart-form for every file
      const formData: FormData = new FormData();
      formData.append('licenseFile', file, file.name);

      // Tell it to report the upload progress
      const req = new HttpRequest('POST', this.licenseDetailPath, formData, {
        reportProgress: true
      });

      const progressSubject = new Subject<number>();

      this.createRequestObservable$(req).subscribe(
        event => {
          if (event.type === HttpEventType.UploadProgress) {
            const percentDone = Math.round((100 * event.loaded) / event.total);
            progressSubject.next(percentDone);
          } else if (event instanceof HttpResponse) {
            // Close the progress-stream if we get an answer form the API
            progressSubject.complete();
          }
        },
        error => {
          console.error('License upload unsuccessful', error);
          if (error.status === 400) {
            const msg = this.parseErrorMessage(error);
            status[file.name].error = msg;
          }

          progressSubject.complete();
          this.notificationService.onFail('License is not saved');
        }
      );

      status[file.name] = {
        progress: progressSubject.asObservable(),
        error: null
      };
    });

    // return the map of progress.observables
    return status;
  }
  //eslint-disable-next-line
  setLicenseData(data): void {
    this.activeTestLicenseDetails.next(data);
  }

  setJmepTrafficLicense(status: boolean): void {
    this.isJmepResultsLicenseEnabled = status;
  }

  getJmepTrafficLicense(): boolean {
    return this.isJmepResultsLicenseEnabled;
  }

  getLicenseVersionValue$(): any {
    const options = {
      headers: new HttpHeaders({
        Accept: '*/*',
        'Content-Type': 'text/plain'
      }),
      responseType: 'text' as 'text'
    };
    return this.http.post('/uicomm/softwareVersion', {}, options);
  }

  //eslint-disable-next-line
  private parseErrorMessage(error: any): string {
    let errorMsg = ERROR_MESSAGE.common;
    if (Array.isArray(error.error?.errors)) {
      const responseMessage = error.error.errors[0];
      errorMsg = responseMessage.includes('invalid')
        ? ERROR_MESSAGE.invalid
        : responseMessage.includes('already added')
        ? ERROR_MESSAGE.added
        : errorMsg;
    }
    return errorMsg;
  }

  private createRequestObservable$(req): Observable<HttpEvent<ProgressEvent>> {
    return this.http.request(req);
  }
}
